import React, { FC, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AppState } from '../../../store/appState';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import Utils from '../../../shared/utils';
import { useAccounts } from '../../checkout/api/accounts/useAccounts';
import { useCartCheckoutMeta } from './api/meta/useCartCheckoutMeta';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { CheckoutSummary, FormDataConfirm } from '../../checkout/CheckoutSummary';
import { CHECKOUT_VIEW_SORT_BY } from '../../../shared/common/SortByDropDown';
import CheckoutListItem, { CheckoutListHeader } from '../../checkout/CheckoutListItem';
import ProductList from '../../../shared/common/ProductList';
import styled from 'styled-components';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import Pagination from '../../../shared/common/Pagination';
import FilterBar from '../../../shared/common/FilterBar';
import { useCartCheckout } from './api/order/useCartCheckout';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { fetchCartAccountSuggestion } from './api/meta/apiFetchAccountSuggestion';
import { useTranslation } from 'react-i18next';
import { useShoppingCartProducts } from '../../../api/shopping-cart/useShoppingCartProducts';
import { GROUPBY_PRODUCTS } from '../../../store/constants';

const CheckoutContainerInner = styled(ContainerInnerWithColumns)`
    margin-left: auto;
    margin-right: auto;
`;

export const CartCheckout: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);

    const history = useHistory();

    const {
        data: accountList,
        selectedAccount,
        onAccountChange,
        isLoading: isLoadingAccounts,
        isError: isErrorAccounts
    } = useAccounts(fetchCartAccountSuggestion);

    const { data: meta, isLoading: isMetaLoading, isError: isMetaError } = useCartCheckoutMeta();
    const {
        page: currentPage,
        setPage: onPageChange,
        setSortBy: onSortChange,
        pageSize,
        sortBy
    } = usePaginationAndSortParams({
        defaultPageSize: 55,
        defaultSortBy: CHECKOUT_VIEW_SORT_BY[0].key,
        shouldResetPageOnSortChange: true
    });

    const { data: cartCheckoutProducts, isLoading: isProductsLoading } = useShoppingCartProducts({
        pagination: {
            sortBy,
            page: currentPage,
            pageSize
        },
        filterMap: {},
        groupType: GROUPBY_PRODUCTS,
        groupId: null
    });
    const products = cartCheckoutProducts?.hits ?? [];

    const { mutate: confirmCheckout, isLoading: isLoadingConfirmCheckout } = useCartCheckout();
    const onConfirmation = (formData: FormDataConfirm) => {
        confirmCheckout({ meta, formData });
    };

    useLayoutEffect(() => {
        if (!Utils.isCustomerAllowedToOrder(authentication)) {
            return history.push('/catalogue');
        }
    }, [dispatch, authentication, history]);

    if (isLoadingAccounts || isMetaLoading || isProductsLoading) {
        return (
            <LoadingWrapper>
                <LoadingOverlay loading={true} />
            </LoadingWrapper>
        );
    }

    const spoc = authentication.spoc;
    const totalProducts = cartCheckoutProducts?.totalHits ?? 0;
    const accounts = {
        list: accountList,
        selected: selectedAccount,
        loading: isLoadingAccounts
    };

    const standingOrders = selectedAccount?.standingOrders
        ? {
              list: selectedAccount.standingOrders,
              selected: selectedAccount.standingOrders[0],
              show: true
          }
        : undefined;

    return (
        <DocumentTitle title={t('components.documentTitle.checkout')}>
            <CheckoutContainerInner className="bw-wrap medium">
                <div className="txt-upper">{t('shop.orders.orderSummary')}</div>
                <div className="content-area-inner">
                    <CheckoutSummary
                        {...meta}
                        accounts={accounts}
                        authentication={authentication}
                        onAccountChange={onAccountChange}
                        onConfirmation={onConfirmation}
                        isPlaceOrderLoading={isLoadingConfirmCheckout}
                        isPlaceOrderDisabled={isMetaError || isErrorAccounts}
                        standingOrders={standingOrders}
                    />
                    <div className="checkout-pl">
                        <FilterBar
                            sortBy={sortBy}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onSortChange={onSortChange}
                            onPageChange={onPageChange}
                            totalProducts={totalProducts}
                            isFilterToggleReplaced={true}
                            leftColumnText={t('shop.orders.items')}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            dispatch={dispatch}
                            hideSelectAll={true}
                            hideDisplayType={true}
                            sorting={CHECKOUT_VIEW_SORT_BY}
                        />
                        <CheckoutListHeader spoc={spoc} />
                        <ProductList
                            products={products}
                            totalProducts={totalProducts}
                            displayType="list"
                            listItemComponent={CheckoutListItem}
                            currentPage={currentPage}
                        />
                        <Pagination
                            className="clear"
                            totalItems={totalProducts}
                            onPageChange={onPageChange}
                            currentPage={currentPage}
                            pageSize={pageSize}
                        />
                    </div>
                </div>
            </CheckoutContainerInner>
        </DocumentTitle>
    );
};
